import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, firstValueFrom, of, timeout } from 'rxjs';
import { environment } from '../../../../environments/environment';

export interface VersionInfo {
  version: string;
  activeProfile?: string;
  activeProfiles?: string[];
  environment: string;
  isLive: boolean;
  timestamp?: string;
}

export interface VersionApiResponse {
  success?: boolean;
  code?: string;
  message?: string;
  data?: VersionInfo;
  localMessage?: {
    eng?: string;
    np?: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class VersionService {
  public readonly versionInfo = signal<VersionInfo | null>(null);
  public readonly version = signal<string>('0.0.1');
  public readonly environmentName = signal<string>('test');
  public readonly isLive = signal<boolean>(false);

  private originalConsoleLog = console.log;
  private originalConsoleInfo = console.info;
  private originalConsoleWarn = console.warn;
  private originalConsoleDebug = console.debug;
  private originalConsoleTrace = console.trace;

  constructor(private http: HttpClient) { }

  public async fetchVersionInfo(): Promise<void> {
    const versionUrl = `${environment.baseApiUrl}/version`;

    try {
      const response = await firstValueFrom(
        this.http.get<VersionApiResponse>(versionUrl).pipe(
          timeout(4000),
          catchError((err) => {
            console.error('Failed to fetch /version config:', err);
            return of(null);
          }),
        ),
      );

      if (response && response.data) {
        const data = response.data;
        this.versionInfo.set(data);
        this.version.set(data.version || '0.0.1');
        const envName =
          data.environment ||
          data.activeProfile ||
          (data.activeProfiles && data.activeProfiles[0]) ||
          'test';
        this.environmentName.set(envName);
        const isLiveValue = data.isLive ?? false;
        this.isLive.set(isLiveValue);
        this.applyConsoleLoggingRules(isLiveValue);
      } else {
        // Fallback when API fails or returns null: app runs normally
        this.isLive.set(false);
        this.environmentName.set('test');
        this.applyConsoleLoggingRules(false);
      }
    } catch (e) {
      // Catch any unexpected error to ensure application runs normally
      console.error('Error initializing version info:', e);
      this.isLive.set(false);
      this.environmentName.set('test');
      this.applyConsoleLoggingRules(false);
    }
  }

  private applyConsoleLoggingRules(isLive: boolean): void {
    if (isLive) {
      // Suppress all non-error console output; keep console.error intact
      const noop = () => { };
      console.log = noop;
      console.info = noop;
      console.warn = noop;
      console.debug = noop;
      console.trace = noop;
    } else {
      // Restore original console methods if previously suppressed
      console.log = this.originalConsoleLog;
      console.info = this.originalConsoleInfo;
      console.warn = this.originalConsoleWarn;
      console.debug = this.originalConsoleDebug;
      console.trace = this.originalConsoleTrace;
    }
  }
}
